/**
 * Single source of truth for building product URLs.
 *
 * URL format:
 *   /product/<slug>-<cas>            SPA-interactive
 *   /catalog/<slug>-<cas>.html       static SEO landing
 *
 * The slug is the product name normalised to lowercase kebab-case ASCII.
 * The CAS is appended at the end (not the start) so a URL like
 *   /product/l-glutamine-56-85-9
 * reads name-first for humans and Googlebot, while `parseProductPath`
 * pulls the CAS off the tail deterministically — even when the name
 * itself contains hyphens (`bpc-157`, `4-dma-78-dhf`, etc.).
 *
 * Why not a random slug? A slug tied to the product name only changes
 * when the name changes, so the canonical URL is stable. The trailing
 * CAS lets us look products up in the DB without maintaining a slug
 * table, and disambiguates rare cases where two products share a name
 * ("Amoxicillin USP" vs "Amoxicillin monohydrate") — the CAS differs.
 *
 * Old URLs (/product/<cas>, /catalog/<cas>.html) are 301-redirected
 * to the new form by .htaccess so already-indexed Google URLs
 * consolidate onto the canonical.
 */

/** Cover the CAS-Registry format `\d{2,7}-\d{2}-\d` plus our INT-style
 *  internal identifiers (`INT-BOLD-PROP` etc.). Both are what we ship
 *  as `cas_number` in the DB. */
const CAS_TAIL_RE = /-(\d{2,7}-\d{2}-\d|INT-[A-Z0-9-]+)$/;

/** ASCII-safe slug for product names. */
export function slugifyProductName(name: string): string {
  return String(name || 'product')
    .toLowerCase()
    .normalize('NFKD')
    /* Strip diacritics that survived normalisation (β→beta, ü→u handled;
       everything else non-ASCII is stripped by the next regex). */
    .replace(/[^\x00-\x7F]/g, '')
    /* Turn any run of non-alphanumerics into a single hyphen. */
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    /* Keep the slug reasonable — very long molecule names (some IUPAC
       spellings are 60+ chars) would balloon the URL past what SERPs
       display comfortably. 60 chars still leaves room for a 12-char CAS. */
    .slice(0, 60)
    .replace(/-+$/, '');
}

/** Product-page URL for the SPA. Pass `absolute: true` to prefix the origin. */
export function productUrl(
  product: { name: string; casNumber?: string; cas_number?: string },
  opts: { absolute?: boolean; origin?: string } = {}
): string {
  const cas = product.casNumber || product.cas_number || '';
  const slug = slugifyProductName(product.name);
  const path = `/product/${slug}-${cas}`;
  return opts.absolute ? `${(opts.origin || '').replace(/\/$/, '')}${path}` : path;
}

/** Static-catalog-page URL. Used as canonical + SEO landing target. */
export function catalogUrl(
  product: { name: string; casNumber?: string; cas_number?: string },
  opts: { absolute?: boolean; origin?: string } = {}
): string {
  const cas = product.casNumber || product.cas_number || '';
  const slug = slugifyProductName(product.name);
  const path = `/catalog/${slug}-${cas}.html`;
  return opts.absolute ? `${(opts.origin || '').replace(/\/$/, '')}${path}` : path;
}

/**
 * Parse the trailing CAS out of a URL path segment.
 *
 * Handles all four shapes we ship, so old bookmarks continue to work:
 *   l-glutamine-56-85-9   → { cas: "56-85-9", slug: "l-glutamine" }
 *   56-85-9               → { cas: "56-85-9", slug: null }
 *   INT-BOLD-PROP         → { cas: "INT-BOLD-PROP", slug: null }
 *   bpc-157-137525-51-0   → { cas: "137525-51-0", slug: "bpc-157" }
 *
 * Returns null if no CAS-looking tail is found — the route handler
 * should then render the 404 view with `noindex`.
 */
export function parseProductPath(pathSegment: string): { cas: string; slug: string | null } | null {
  if (!pathSegment) return null;
  const raw = decodeURIComponent(pathSegment).replace(/^\/+|\/+$/g, '').replace(/\.html?$/, '');
  /* Case A — the whole segment IS a bare CAS (bare integer-hyphen form or INT-…). */
  if (/^\d{2,7}-\d{2}-\d$/.test(raw)) return { cas: raw, slug: null };
  if (/^INT-[A-Z0-9-]+$/.test(raw)) return { cas: raw, slug: null };
  /* Case B — the segment ends with a CAS-shaped tail; strip it off. */
  const m = raw.match(CAS_TAIL_RE);
  if (m) {
    const cas = m[1];
    const slug = raw.slice(0, raw.length - cas.length - 1) || null;
    return { cas, slug };
  }
  return null;
}
